import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import Toast from '../Toast';

const SubscriptionManagement = ({ organizationId }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateForm, setDateForm] = useState({ startDate: '', endDate: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (organizationId) {
      loadSubscription();
    }
  }, [organizationId]);

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const token = authService.getToken();
      const resp = await fetch(`/api/admin/billing/subscription/${organizationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error('Failed to load subscription');
      const data = await resp.json();
      setSubscription(data);
    } catch (e) {
      showToast(e.message || 'Failed to load subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 5000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const handleViewStripeDashboard = () => {
    if (!subscription?.stripeCustomerId) {
      showToast('No Stripe Customer ID available', 'error');
      return;
    }
    const stripeDashboardUrl = `https://dashboard.stripe.com/customers/${subscription.stripeCustomerId}`;
    window.open(stripeDashboardUrl, '_blank');
  };

  const handleCreatePortal = async () => {
    setUpdating(true);
    try {
      const token = authService.getToken();
      const resp = await fetch(`/api/admin/billing/subscription/${organizationId}/stripe-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed to create portal session');
      window.open(data.url, '_blank');
      showToast('Portal session created', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to create portal session', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDates = async () => {
    if (!dateForm.startDate || !dateForm.endDate) {
      showToast('Please provide both start and end dates', 'error');
      return;
    }
    setUpdating(true);
    try {
      const token = authService.getToken();
      const resp = await fetch(`/api/admin/billing/subscription/${organizationId}/update-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          startDate: new Date(dateForm.startDate).toISOString(),
          endDate: new Date(dateForm.endDate).toISOString()
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed to update dates');
      setShowDateModal(false);
      setDateForm({ startDate: '', endDate: '' });
      showToast('Dates updated successfully', 'success');
      loadSubscription();
    } catch (e) {
      showToast(e.message || 'Failed to update dates', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendNotification = async (isTest = false) => {
    setUpdating(true);
    try {
      const token = authService.getToken();
      const resp = await fetch(`/api/admin/billing/subscription/${organizationId}/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          isTestEmail: isTest,
          testEmail: isTest ? subscription?.stripeData?.customer?.email : undefined
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed to send notification');
      showToast(data.message || 'Notification sent successfully', 'success');
      loadSubscription();
    } catch (e) {
      showToast(e.message || 'Failed to send notification', 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <p>Loading subscription details...</p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <p>No subscription data available</p>
      </div>
    );
  }

  // Calculate timeline positions
  const now = new Date();
  const periodStart = subscription.currentPeriodStart ? new Date(subscription.currentPeriodStart) : null;
  const periodEnd = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const graceEnd = subscription.gracePeriodEnd ? new Date(subscription.gracePeriodEnd) : null;
  const warningDate = periodEnd ? new Date(periodEnd.getTime() - 5 * 24 * 60 * 60 * 1000) : null; // 5 days before end

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '8px', fontSize: '20px', fontWeight: 600 }}>
          {subscription.organizationName}
        </h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Organization ID: {subscription.organizationId}</p>
      </div>

      {/* Status Badges */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <span
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            background: subscription.status === 'Active' ? '#d1fae5' : '#fee2e2',
            color: subscription.status === 'Active' ? '#065f46' : '#991b1b'
          }}
        >
          {subscription.status}
        </span>
        {subscription.isExpiringSoon && (
          <span
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              background: '#fef3c7',
              color: '#92400e'
            }}
          >
            ⚠️ Expiring Soon
          </span>
        )}
        {subscription.isInGracePeriod && (
          <span
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              background: '#fed7aa',
              color: '#9a3412'
            }}
          >
            ⏱️ Grace Period Active
          </span>
        )}
        <span
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            background: subscription.tier === 'Enterprise' ? '#ddd6fe' : '#dbeafe',
            color: subscription.tier === 'Enterprise' ? '#5b21b6' : '#1e40af'
          }}
        >
          {subscription.tier}
        </span>
      </div>

      {/* Subscription Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>Billing Cycle</h3>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{subscription.billingCycle}</p>
        </div>
        <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>Monthly Amount</h3>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{formatCurrency(subscription.monthlyAmount)}</p>
        </div>
        <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>Last Notification</h3>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{subscription.lastNotificationMessage}</p>
        </div>
      </div>

      {/* Timeline */}
      {periodStart && periodEnd && (
        <div style={{ marginBottom: '24px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Subscription Timeline</h3>
          <div style={{ position: 'relative', height: '80px', marginBottom: '16px' }}>
            {/* Timeline bar */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '4px',
                background: '#e5e7eb',
                transform: 'translateY(-50%)'
              }}
            />
            
            {/* Period start marker */}
            <div style={{ position: 'absolute', left: '0%', top: '50%', transform: 'translate(-50%, -50%)' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#10b981',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
              <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '12px', color: '#6b7280' }}>
                Start
              </div>
              <div style={{ position: 'absolute', top: '32px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '11px', color: '#9ca3af' }}>
                {formatDate(subscription.currentPeriodStart)}
              </div>
            </div>

            {/* 5-day warning marker */}
            {warningDate && warningDate > now && (
              <div style={{ position: 'absolute', left: '75%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#f59e0b',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '11px', color: '#92400e' }}>
                  5-day warning
                </div>
              </div>
            )}

            {/* Period end marker */}
            <div style={{ position: 'absolute', left: '100%', top: '50%', transform: 'translate(-50%, -50%)' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: subscription.isExpiringSoon ? '#ef4444' : '#6b7280',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
              <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '12px', color: '#6b7280' }}>
                End
              </div>
              <div style={{ position: 'absolute', top: '32px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '11px', color: '#9ca3af' }}>
                {formatDate(subscription.currentPeriodEnd)}
              </div>
            </div>

            {/* Grace period end marker */}
            {graceEnd && (
              <div style={{ position: 'absolute', left: '120%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: subscription.isInGracePeriod ? '#f97316' : '#d1d5db',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '11px', color: '#9a3412' }}>
                  Grace ends
                </div>
                <div style={{ position: 'absolute', top: '32px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '11px', color: '#9ca3af' }}>
                  {formatDate(subscription.gracePeriodEnd)}
                </div>
              </div>
            )}

            {/* Current position indicator */}
            {periodStart && periodEnd && now >= periodStart && now <= periodEnd && (
              <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: '#3b82f6',
                    border: '3px solid #fff',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.5)'
                  }}
                />
              </div>
            )}
          </div>

          {/* Timeline labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
            <div>
              <strong>Current Period:</strong> {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
            </div>
            {graceEnd && (
              <div>
                <strong>Grace Period Ends:</strong> {formatDateTime(subscription.gracePeriodEnd)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stripe IDs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {subscription.stripeCustomerId && (
          <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>Stripe Customer ID</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <code style={{ flex: 1, padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }}>
                {subscription.stripeCustomerId}
              </code>
              <button
                onClick={() => copyToClipboard(subscription.stripeCustomerId)}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Copy
              </button>
            </div>
          </div>
        )}
        {subscription.stripeSubscriptionId && (
          <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>Stripe Subscription ID</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <code style={{ flex: 1, padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }}>
                {subscription.stripeSubscriptionId}
              </code>
              <button
                onClick={() => copyToClipboard(subscription.stripeSubscriptionId)}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Actions</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleViewStripeDashboard}
            disabled={!subscription.stripeCustomerId}
            style={{
              padding: '10px 20px',
              background: subscription.stripeCustomerId ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: subscription.stripeCustomerId ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            View in Stripe Dashboard
          </button>
          <button
            onClick={handleCreatePortal}
            disabled={!subscription.stripeCustomerId || updating}
            style={{
              padding: '10px 20px',
              background: subscription.stripeCustomerId && !updating ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: subscription.stripeCustomerId && !updating ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            {updating ? 'Creating...' : 'Create Customer Portal'}
          </button>
          <button
            onClick={() => setShowDateModal(true)}
            disabled={updating}
            style={{
              padding: '10px 20px',
              background: !updating ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !updating ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            Manual Date Sync
          </button>
          <button
            onClick={() => handleSendNotification(false)}
            disabled={updating}
            style={{
              padding: '10px 20px',
              background: !updating ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !updating ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            Send Expiration Notice
          </button>
          <button
            onClick={() => handleSendNotification(true)}
            disabled={updating}
            style={{
              padding: '10px 20px',
              background: !updating ? '#f59e0b' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !updating ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            Test Email
          </button>
        </div>
      </div>

      {/* Date Update Modal */}
      {showDateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => !updating && setShowDateModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>
              Manual Date Sync
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280' }}>
              Manually update subscription period dates if webhook missed synchronization.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              <label>
                <span style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>Start Date</span>
                <input
                  type="datetime-local"
                  value={dateForm.startDate}
                  onChange={(e) => setDateForm({ ...dateForm, startDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>End Date</span>
                <input
                  type="datetime-local"
                  value={dateForm.endDate}
                  onChange={(e) => setDateForm({ ...dateForm, endDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDateModal(false)}
                disabled={updating}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  color: '#374151',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: updating ? 'not-allowed' : 'pointer',
                  opacity: updating ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateDates}
                disabled={updating || !dateForm.startDate || !dateForm.endDate}
                style={{
                  padding: '10px 20px',
                  background: updating || !dateForm.startDate || !dateForm.endDate ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: updating || !dateForm.startDate || !dateForm.endDate ? 'not-allowed' : 'pointer'
                }}
              >
                {updating ? 'Updating...' : 'Update Dates'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={toast.show} type={toast.type} message={toast.message} onClose={() => setToast({ show: false, type: 'success', message: '' })} />
    </div>
  );
};

export default SubscriptionManagement;

